/**
 * Checks if a file path or URL is a video source based on file extension.
 *
 * @param {string} source - The file path or URL to check
 * @returns {boolean} True if the source is a video file, false otherwise
 *
 * @example
 * isVideoSource("video.mp4")  // returns true
 * isVideoSource("video.webm") // returns true
 * isVideoSource("video.mp4?v=123") // returns true (ignores query params)
 * isVideoSource("image.jpg")  // returns false
 * isVideoSource("")           // returns false
 */
export const isVideoSource = (source = "") =>
  /\.(mp4|webm|ogg|mov)$/i.test((source || "").split("?")[0]);

/**
 * Determines the MIME type for a video file based on its extension.
 *
 * @param {string} source - The video file path or URL
 * @returns {string} The MIME type for the video (default: "video/webm")
 *
 * @example
 * getVideoMimeType("video.mp4")  // returns "video/mp4"
 * getVideoMimeType("video.webm") // returns "video/webm"
 * getVideoMimeType("video.mov")  // returns "video/quicktime"
 * getVideoMimeType("video.ogg")  // returns "video/ogg"
 * getVideoMimeType("unknown.xyz") // returns "video/webm" (default)
 */
export const getVideoMimeType = (source = "") => {
  const normalized = (source || "").split("?")[0].toLowerCase();

  if (normalized.endsWith(".webm")) return "video/webm";
  if (normalized.endsWith(".mp4")) return "video/mp4";
  if (normalized.endsWith(".ogg")) return "video/ogg";
  if (normalized.endsWith(".mov")) return "video/quicktime";

  return "video/webm";
};

/**
 * Checks if a file path or URL is an audio source based on file extension.
 *
 * @param {string} source - The file path or URL to check
 * @returns {boolean} True if the source is an audio file, false otherwise
 *
 * @example
 * isAudioSource("song.mp3")  // returns true
 * isAudioSource("song.wav")  // returns true
 * isAudioSource("video.mp4") // returns false
 */
export const isAudioSource = (source = "") =>
  /\.(mp3|wav|ogg|m4a|aac|flac)$/i.test((source || "").split("?")[0]);

/**
 * Determines the MIME type for an audio file based on its extension.
 *
 * @param {string} source - The audio file path or URL
 * @returns {string} The MIME type for the audio (default: "audio/mpeg")
 *
 * @example
 * getAudioMimeType("song.mp3") // returns "audio/mpeg"
 * getAudioMimeType("song.wav") // returns "audio/wav"
 * getAudioMimeType("song.ogg") // returns "audio/ogg"
 */
export const getAudioMimeType = (source = "") => {
  const normalized = (source || "").split("?")[0].toLowerCase();

  if (normalized.endsWith(".mp3")) return "audio/mpeg";
  if (normalized.endsWith(".wav")) return "audio/wav";
  if (normalized.endsWith(".ogg")) return "audio/ogg";
  if (normalized.endsWith(".m4a")) return "audio/mp4";
  if (normalized.endsWith(".aac")) return "audio/aac";
  if (normalized.endsWith(".flac")) return "audio/flac";

  return "audio/mpeg";
};
